import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from "@mui/material";
import { Add, Edit, Delete, Close, Save } from "@mui/icons-material";
import { toast } from "react-toastify";
import theme from "../../theme/theme";
import TableComponent from "../../components/Table";
import ConfirmDialog from "../../components/ConfirmDialog";
import {
  fetchBanks,
  createBank,
  updateBank,
  deleteBank,
} from "../../api/BankService";

const emptyForm = () => ({
  name: "",
  account_holder: "",
  account_number: "",
  ifsc_code: "",
  branch: "",
});

const BanksManagementPage = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadBanks = async () => {
    setLoading(true);
    try {
      const response = await fetchBanks();
      const data = response.data?.data || response.data || [];
      setBanks(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message || "Failed to fetch banks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBanks();
  }, []);

  const openCreate = () => {
    setEditingBank(null);
    setForm(emptyForm());
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (bank) => {
    setEditingBank(bank);
    setForm({
      name: bank.name || "",
      account_holder: bank.account_holder || "",
      account_number: bank.account_number || "",
      ifsc_code: bank.ifsc_code || "",
      branch: bank.branch || "",
    });
    setErrors({});
    setDialogOpen(true);
  };

  const setField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    setErrors({});
    try {
      const res = editingBank
        ? await updateBank(editingBank.id, form)
        : await createBank(form);
      if (res?.status === "error") {
        if (res.errors) setErrors(res.errors);
        toast.error(res.message || "Failed to save bank");
        return;
      }
      toast.success(editingBank ? "Bank updated successfully" : "Bank created successfully");
      setDialogOpen(false);
      loadBanks();
    } catch (error) {
      if (error?.data?.errors) {
        setErrors(error.data.errors);
      } else {
        toast.error(error.message || "Failed to save bank");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteBank(deleteTarget.id);
      if (res?.status === "error") {
        toast.error(res.message || "Failed to delete bank");
      } else {
        toast.success("Bank deleted successfully");
        setConfirmOpen(false);
        setDeleteTarget(null);
        loadBanks();
      }
    } catch (error) {
      toast.error(error?.message || "Failed to delete bank");
    } finally {
      setDeleting(false);
    }
  };

  const err = (field) => (errors[field] ? errors[field][0] : "");

  const columns = [
    { field: "name", headerName: "Bank Name" },
    { field: "account_holder", headerName: "Account Holder" },
    { field: "account_number", headerName: "Account Number" },
    { field: "ifsc_code", headerName: "IFSC Code" },
    { field: "branch", headerName: "Branch" },
    {
      field: "services_count",
      headerName: "Linked Services",
      renderCell: (row) => row.services_count || 0,
    },
  ];

  const actions = [
    {
      icon: <Edit />,
      label: "Edit Bank",
      onClick: (row) => openEdit(row),
      backgroundColor: theme.button.editBackgroundColor || "#f0a500",
    },
    {
      icon: <Delete />,
      label: "Delete Bank",
      onClick: (row) => {
        setDeleteTarget(row);
        setConfirmOpen(true);
      },
      backgroundColor: theme.button.deleteBackgroundColor || "#ff5252",
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box component="h1" sx={{ m: 0 }}>
          Banks Management
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          sx={{
            borderRadius: theme.button.borderRadius,
            backgroundColor: theme.palette.primary.main,
          }}
          onClick={openCreate}
        >
          Add Bank
        </Button>
      </Box>

      <TableComponent
        data={banks || []}
        columns={columns}
        actions={actions}
        loading={loading}
        links={[]}
        pagination={false}
      />

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: theme.dialog.borderRadius } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 700,
          }}
        >
          {editingBank ? "Edit Bank" : "Add Bank"}
          <IconButton onClick={() => setDialogOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              size="small"
              label="Bank Name *"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              error={!!err("name")}
              helperText={err("name")}
            />
            <TextField
              size="small"
              label="Account Holder"
              placeholder="e.g. RAINBOW PROJECTS"
              value={form.account_holder}
              onChange={(e) => setField("account_holder", e.target.value)}
              error={!!err("account_holder")}
              helperText={err("account_holder")}
            />
            <TextField
              size="small"
              label="Account Number *"
              value={form.account_number}
              onChange={(e) => setField("account_number", e.target.value)}
              error={!!err("account_number")}
              helperText={err("account_number")}
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                size="small"
                label="IFSC Code"
                value={form.ifsc_code}
                onChange={(e) => setField("ifsc_code", e.target.value)}
                error={!!err("ifsc_code")}
                helperText={err("ifsc_code")}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Branch"
                value={form.branch}
                onChange={(e) => setField("branch", e.target.value)}
                error={!!err("branch")}
                helperText={err("branch")}
                sx={{ flex: 1 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={saving}
            variant="outlined"
            sx={{
              borderRadius: theme.button.borderRadius,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !form.name.trim() || !form.account_number.trim()}
            variant="contained"
            startIcon={<Save />}
            sx={{
              borderRadius: theme.button.borderRadius,
              textTransform: "none",
              fontWeight: 600,
              backgroundColor: theme.palette.primary.main,
            }}
          >
            {saving ? "Saving..." : editingBank ? "Update Bank" : "Create Bank"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Bank"
        message={`Are you sure you want to delete bank "${deleteTarget?.name}"? Banks linked to service contracts cannot be deleted.`}
        loading={deleting}
      />
    </Box>
  );
};

export default BanksManagementPage;
